import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';
import fr from './locales/fr/translation.json';
import de from './locales/de/translation.json';
import th from './locales/th/translation.json';

// The UI languages Autolang ships translations for. A user's preferred language
// (a full locale such as en_US) is reduced to its language code to pick the bundle;
// anything without a bundle falls back to English (see setLanguageFromLocale).
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'th'];

// Human-readable, self-referential names for the language switcher, so each option
// reads in its own language regardless of the active UI language.
export const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  th: 'ไทย',
};

// Where the chosen UI language is remembered between visits, before (and until) an
// authenticated user's stored preferred language takes over.
export const LANGUAGE_STORAGE_KEY = 'autolang.language';

// A representative full locale for each UI language. The preferred language is
// stored as a full Locale ({ languageCode, countryCode }), but the language
// switcher only yields a bare language code, so this map supplies a default
// country when persisting the choice. The country is only a seed: the
// student-locale default matches on the language code first (see Chat.js), so the
// exact country here is not critical.
export const DEFAULT_LOCALE_FOR_LANGUAGE = {
  en: { languageCode: 'en', countryCode: 'US' },
  fr: { languageCode: 'fr', countryCode: 'FR' },
  de: { languageCode: 'de', countryCode: 'DE' },
  th: { languageCode: 'th', countryCode: 'TH' },
};

// Turns a bare UI language code into the full Locale to persist as the user's
// preferred language, falling back to English when the code is unknown.
export function localeForLanguage(language) {
  return DEFAULT_LOCALE_FOR_LANGUAGE[language] || DEFAULT_LOCALE_FOR_LANGUAGE.en;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      de: { translation: de },
      th: { translation: th },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // Treat regional variants (en-GB, fr-CA, …) as their base language so a browser
    // set to en-GB still resolves to the `en` bundle rather than falling back.
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes values against XSS.
    },
  });

// Applies a LocaleDto ({ languageCode, countryCode }) as the UI language by using
// its language code, falling back to English when no bundle is shipped for it. Used
// to sync the UI with a user's stored preferred language after login.
export function setLanguageFromLocale(locale) {
  if (!locale || !locale.languageCode) {
    return;
  }
  const language = SUPPORTED_LANGUAGES.includes(locale.languageCode)
    ? locale.languageCode
    : 'en';
  i18n.changeLanguage(language);
}

export default i18n;
